/**
* @license Apache-2.0
*
* Copyright (c) 2026 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isUint64 from '@stdlib/assert-is-uint64';
import { assign as toWords } from '@stdlib/number-uint64-base-to-words';
import Uint64 from '@stdlib/number-uint64-ctor';
import format from '@stdlib/error-tools-fmtprodmsg';


// MAIN //

/**
* Returns an array of iterated values.
*
* @private
* @param {Object} it - iterator
* @param {Function} clbk - callback to invoke for each iterated value
* @param {*} thisArg - invocation context
* @returns {(Array|TypeError)} array or an error
*/
function fromIteratorMap( it, clbk, thisArg ) {
	var words;
	var out;
	var v;
	var u;
	var i;

	// Workspace for storing high and low words:
	words = [ 0, 0 ];

	out = [];
	i = -1;
	while ( true ) {
		v = it.next();
		if ( v.done ) {
			break;
		}
		i += 1;
		u = clbk.call( thisArg, v.value, i );
		if ( !isUint64( u ) ) {
			try {
				u = new Uint64( u );
			} catch ( err ) { // eslint-disable-line no-unused-vars
				return new TypeError( format( 'invalid argument. An iterator must return either a 64-bit unsigned integer or a value which can be converted to a 64-bit unsigned integer. Value: `%s`.', u ) );
			}
		}
		toWords( u, words, 1, 0 );
		out.push( words[ 0 ], words[ 1 ] );
	}
	return out;
}


// EXPORTS //

export default fromIteratorMap;
